import { apiFetch, CallApiParams } from "./apiFetch";

/**
 * A wrapper around `apiFetch` that provides the same external interface as the
 * legacy `callApi` function from @web-monorepo/infra. This allows for a gradual
 * migration to the fetch-based implementation while maintaining API compatibility.
 *
 * Unlike `apiFetch` it throws on failure, so non-2xx responses reject rather than
 * resolving as success.
 *
 * @param {CallApiParams} params - The parameters for the API call.
 * @returns {Promise<Response>} A promise that resolves to a Response-like object.
 * @throws {Error} Throws an error if the request fails.
 */
export async function callApi({
  method = "GET",
  path,
  body,
  query = {},
  headers = {},
  route,
  credentials,
  retries,
  external,
}: CallApiParams) {
  const result = await apiFetch({
    method,
    path,
    body,
    query,
    headers,
    route,
    credentials,
    retries,
    external,
  });

  if (!result.success) {
    const error = result.error as Error & {
      response?: { body?: { error?: { detail?: { isReadOnlySessionError?: boolean } } } };
      isExpected?: boolean;
    };
    if (error.response?.body?.error?.detail?.isReadOnlySessionError) {
      error.isExpected = true;
    }
    throw error;
  }

  const req = { url: path, method, path, query, body, headers, route, credentials };
  return {
    // Unfortunately, for compatibility with Superagent (which has data: any),
    // we're stuck with this in the compatibility layer: typing `body` as `unknown`
    // would force a narrowing cast at every `callApi(...).body.x` site across the
    // monorepo. Tracked for incremental fixing at the call sites.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- compat-layer body type; see comment above
    body: result.data as any,
    text: result.text,
    status: result.status,
    statusCode: result.status,
    headers: result.headers,
    req,
    request: req,
  };
}

export type CallApiReturnType = Awaited<ReturnType<typeof callApi>>;
